<template>
    <div class="kanban-board">
        <div
            v-show="!isLoading"
            class="minimap-container"
            :class="{ 'minimap-collapsed': minimapCollapsed }"
        >
            <div class="minimap-toggle" @click="toggleMinimap">
                <i class="material-icons">{{ minimapCollapsed ? "unfold_more" : "unfold_less" }}</i>
            </div>
            <div class="minimap-content" v-show="!minimapCollapsed">
                <div
                    class="minimap-slider"
                    :style="{ left: scrollBarPosition + 'px', width: scrollBarWidth + 'px' }"
                    @mousedown="startDragging"
                >
                    <div class="material-icons">visibility</div>
                </div>
                <div class="minimap-scroll">
                    <a-tooltip
                        placement="top"
                        v-for="pipeline in filteredPipelines"
                        :title="pipeline.name"
                        :key="pipeline._id"
                        :color="pipeline.color || '#1890ff'"
                    >
                        <div
                            class="minimap-item"
                            :style="{ backgroundColor: pipeline.color || '#1890ff' }"
                            @click="scrollToPipeline(pipeline._id)"
                        ></div>
                    </a-tooltip>
                </div>
            </div>
        </div>

        <draggable
            v-show="!isLoading"
            v-model="pipelines"
            :key="`${workflowId}_${renderKey}`"
            class="supsis-draggable"
            :options="taskGroupDragOptions"
            @start="handleDragStart"
            @end="movePipelineEnd"
            draggable=".drag-pipeline-columns"
        >
            <transition-group tag="div" class="drag-container">
                <TaskColumnCard
                    v-for="pipeline of filteredPipelines"
                    :ref="`pipeline_${pipeline._id}`"
                    :key="pipeline._id"
                    :pipelineLoaded="pipelineLoaded"
                    :pipelines="pipelines"
                    :pipeline="pipeline"
                    :moving="moving"
                    :workflowId="workflowId"
                    :tasks="tasks[pipeline._id]"
                    :checkedItems="checkedItems"
                    :basicTaskForm="basicTaskForm"
                    @loadMoreTask="loadMoreTask"
                    @updateCheckedItems="$emit('updateCheckedItems', $event)"
                    @createdTask="createdTask(pipeline._id, $event)"
                    @updateBasicTaskForm="$emit('updateBasicTaskForm', $event)"
                    @resetActions="resetActions"
                    @close-create-mode="closeCreateMode"
                    @moved="moveActionEnd"
                    @pipeline-form="createPipelineForm"
                    @create-pipeline="createPipeline"
                    @update-pipeline="updatePipeline"
                    @pipeline-deleted="handlePipelineDeleted"
                    @delete-modal-task="openDeleteTaskModal"
                    @confirm-modal-task="confirmModalOpen"
                    @archived-modal-task="archivedModalOpen"
                    @change-workflow-task="changeWorkflowTask"
                    @to-completed-task="toCompletedTask"
                />
            </transition-group>
        </draggable>
        <DeleteModal
            v-if="deleteTaskModal"
            :description="$t('task.deleteTaskCont')"
            :btnCancelText="$t('message.cancel')"
            :btnoktext="$t('message.delete')"
            @cancel="deleteTaskModal = false"
            @confirm="softDeleteTask"
        />
        <ConfirmModal
            v-if="confirmTaskModal"
            :description="$t('task.sureAddCompletedList')"
            :btnCancelText="$t('message.cancel')"
            :btnoktext="$t('message.yes')"
            @cancel="confirmTaskModal = false"
            @confirm="confirmTask"
        />
        <a-modal
            v-model="simpleConfirmModal"
            :title="$t('task.taskCloseModalTitle')"
            :closable="true"
            :maskClosable="true"
            @ok="simpleDoneTask"
            @cancel="simpleConfirmModal = false"
            :okText="$t('task.batchConfirm')"
            :cancelText="$t('message.cancel')"
            :okButtonProps="{ type: 'primary', style: { backgroundColor: '#ec4b1d', borderColor: '#ec4b1d' } }"
        >
            <div class="simple-confirm-modal-content">
                <div class="modal-icon">
                    <span class="material-icons">check_circle</span>
                </div>
                <div class="modal-message">
                    <p>{{ $t("task.taskCloseConfirmQuestion") }}</p>
                    <p class="modal-description">
                        {{ $t("task.taskCloseConfirmDetail") }}
                    </p>
                </div>
            </div>
        </a-modal>
        <TaskArchiveModal
            v-if="archivedTaskModal"
            :description="'Görevi Arşivlemek İstediğinize Emin Misiniz?'"
            :btnCancelText="$t('message.cancel')"
            :btnoktext="$t('message.yes')"
            @cancel="archivedTaskModal = false"
            @confirm="archiveModalTask"
        />
        <a-modal
            v-model="changeWorkflowModal"
            :title="$t('task.taskChangeWorkflowModalTitle')"
            @ok="handleChangeWorkflow"
            @cancel="handleCancel"
        >
            <a-select
                v-model="selectedWorkflow"
                :placeholder="$t('task.taskSelectWorkflowPlaceholder')"
                style="width: 100%"
            >
                <a-select-option v-for="workflow in workflows" :key="workflow._id" :value="workflow._id">
                    {{ workflow.title }}
                </a-select-option>
            </a-select>
        </a-modal>

        <div v-if="isLoading" class="loading-overlay">
            <div class="loading-content">
                <div class="kanban-loader">
                    <svg class="kanban-loader-svg" viewBox="0 0 240 150" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <linearGradient id="kl-shimmer" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stop-color="#eef2f7" />
                                <stop offset="50%" stop-color="#ffffff" />
                                <stop offset="100%" stop-color="#eef2f7" />
                            </linearGradient>
                            <filter id="kl-shadow" x="-20%" y="-20%" width="140%" height="140%">
                                <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#ec4b1d" flood-opacity="0.35" />
                            </filter>
                        </defs>

                        <g class="kl-column kl-col-1">
                            <rect x="10" y="12" width="64" height="128" rx="8" class="kl-col-bg" />
                            <rect x="18" y="22" width="30" height="5" rx="2.5" fill="#ec4b1d" />
                            <circle cx="64" cy="24" r="3" fill="#ec4b1d" opacity="0.7" />
                            <rect x="16" y="36" width="52" height="24" rx="5" class="kl-card" />
                            <rect x="22" y="42" width="28" height="3" rx="1.5" fill="#cbd5e1" />
                            <rect x="22" y="49" width="18" height="2.5" rx="1.25" fill="#e2e8f0" />

                            <rect x="16" y="66" width="52" height="24" rx="5" class="kl-card" />
                            <rect x="22" y="72" width="34" height="3" rx="1.5" fill="#cbd5e1" />
                            <rect x="22" y="79" width="20" height="2.5" rx="1.25" fill="#e2e8f0" />
                        </g>

                        <g class="kl-column kl-col-2">
                            <rect x="88" y="12" width="64" height="128" rx="8" class="kl-col-bg" />
                            <rect x="96" y="22" width="30" height="5" rx="2.5" fill="#fb923c" />
                            <circle cx="142" cy="24" r="3" fill="#fb923c" opacity="0.7" />
                            <rect x="94" y="66" width="52" height="24" rx="5" class="kl-card" />
                            <rect x="100" y="72" width="30" height="3" rx="1.5" fill="#cbd5e1" />
                            <rect x="100" y="79" width="22" height="2.5" rx="1.25" fill="#e2e8f0" />
                        </g>

                        <g class="kl-column kl-col-3">
                            <rect x="166" y="12" width="64" height="128" rx="8" class="kl-col-bg" />
                            <rect x="174" y="22" width="30" height="5" rx="2.5" fill="#22c55e" />
                            <circle cx="220" cy="24" r="3" fill="#22c55e" opacity="0.7" />
                            <rect x="172" y="36" width="52" height="24" rx="5" class="kl-card" />
                            <rect x="178" y="42" width="26" height="3" rx="1.5" fill="#cbd5e1" />
                            <rect x="178" y="49" width="18" height="2.5" rx="1.25" fill="#e2e8f0" />
                        </g>

                        <g class="kl-moving-card" filter="url(#kl-shadow)">
                            <rect width="52" height="24" rx="5" fill="#ec4b1d" />
                            <circle cx="9" cy="12" r="3" fill="#ffffff" />
                            <rect x="16" y="7" width="28" height="3" rx="1.5" fill="#ffffff" opacity="0.95" />
                            <rect x="16" y="14" width="18" height="2.5" rx="1.25" fill="#ffffff" opacity="0.7" />
                        </g>

                        <path class="kl-flow-path" d="M 42 90 Q 80 90 120 66 Q 160 42 198 60"
                            fill="none" stroke="#ec4b1d" stroke-width="1" stroke-dasharray="3 4"
                            opacity="0.35" />
                    </svg>
                </div>
                <div class="loading-text">
                    {{ $t("message.loading") }}
                </div>
                <div class="loading-subtext">
                    {{ $t("task.taskKanbanLoadingSubtext") }}
                </div>
                <div class="loading-dots">
                    <span class="loading-dot"></span>
                    <span class="loading-dot"></span>
                    <span class="loading-dot"></span>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import Draggable from "vuedraggable";
import TaskColumnCard from "./TaskColumnCard.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import DeleteModal from "./DeleteModal.vue";
import ConfirmModal from "./ConfirmModal.vue";
import TaskArchiveModal from "./TaskArchiveModal.vue";
import { sendSocketMessage } from "../../../../utils/socketStream";
import { publishMessage, SystemMessageTypes } from "../../../../utils/systemPubSub";

const COMMANDS = {
    TASK_UPDATED: "task updated",
};

export default {
    name: "TaskDraggable",
    components: {
        Draggable,
        TaskColumnCard,
        DeleteModal,
        ConfirmModal,
        TaskArchiveModal,
    },

    props: {
        workflowId: {
            type: String,
            default: null,
        },
        assignees: {
            type: Array,
            default: () => [],
        },
        owners: {
            type: Array,
            default: () => [],
        },
        followers: {
            type: Array,
            default: () => [],
        },
        createdBy: {
            type: Array,
            default: () => [],
        },
        tasks: {
            type: Object,
            default: () => ({}),
        },
        checkedItems: {
            type: Object,
            default: () => ({}),
        },
        basicTaskForm: {
            type: Object,
            default: () => ({}),
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        retryCount: {
            type: Number,
            default: 0,
        },
        showOverdue: {
            type: Boolean,
            default: false,
        },
        dateRange: {
            type: Array,
            default: null,
        },
        startDateRange: {
            type: Array,
            default: null,
        },
        endDateRange: {
            type: Array,
            default: null,
        },
        updateAtRange: {
            type: Array,
            default: null,
        },
        selectedPipelines: {
            type: Array,
            default: () => [],
        },
        sortBy: {
            type: String,
            default: null,
        },
    },
    computed: {
        ...mapState({
            users: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc[user._id] = user;
                    return acc;
                }, {}),
            workflows: (state) =>
                state.tasks.workflows.reduce((acc, workflow) => {
                    acc[workflow._id] = workflow;
                    return acc;
                }, {}),
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
        tags() {
            return this.$parent && this.$parent.selectedTags ? this.$parent.selectedTags : [];
        },
        filterHash() {
            return JSON.stringify({
                assignees: this.assignees,
                owners: this.owners,
                followers: this.followers,
                createdBy: this.createdBy,
                showOverdue: this.showOverdue,
                startDateRange: this.startDateRange,
                endDateRange: this.endDateRange,
                updateAtRange: this.updateAtRange,
                selectedPipelines: this.selectedPipelines,
                dateRange: this.dateRange
            });
        },
        filteredPipelines() {
            const pipelines = this.workflows[this.workflowId]?.pipeline || this.pipelines;
            if (!this.selectedPipelines || this.selectedPipelines.length === 0) {
                return pipelines;
            }
            return pipelines.filter(pipeline =>
                this.selectedPipelines.includes(pipeline._id)
            );
        },
    },
    data: () => ({
        renderKey: 1,
        pipelines: [],
        localTasks: {},
        scrollBarWidth: 30,
        scrollBarPosition: 0,
        isDragging: false,
        startX: 0,
        boardWidth: 0,
        containerWidth: 0,
        taskGroupDragOptions: {
            group: "taskPipeline",
            animation: 200,
        },
        listeners: [],
        moving: false,
        deleteTaskModal: false,
        confirmTaskModal: false,
        simpleConfirmModal: false,
        archivedTaskModal: false,
        changeWorkflowModal: false,
        selectedWorkflow: null,
        confirmTaskData: {},
        archivedTaskData: {},
        deleteTaskData: {},
        changeWorkflowData: {},
        loadingText: "Yükleniyor...",
        minimapCollapsed: false,
        paginations: {},
        loading: {},
        refillRequestId: 0,
        pipelineLoaded: false,
        isUpdatingFromParent: false,
        // Auto-scroll properties
        autoScrollInterval: null,
        isDraggingTask: false,
        SCROLL_ZONE_SIZE: 200,
        SCROLL_SPEED: 15,
        // Loading sırasında scroll pozisyonunu korumak için
        savedScrollLeft: 0,
    }),
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
            updateTaskPosition: "tasks/updateTaskPosition",
            updatePipelinePosition: "tasks/updatePipelinePosition",
            setWorkflows: "tasks/setWorkflows",
            deleteTask: "tasks/deleteTask",
            createPipelineStage: "tasks/createPipelineStage",
            updatePipelineStage: "tasks/updatePipelineStage",
            doneTask: "tasks/doneTask",
            toggleArchiveTask: "tasks/toggleArchiveTask",
            changeWorkflow: "tasks/changeWorkflow",
            getPipelineTasks: "tasks/getPipelineTasks",
            getWorkflowTasks: "tasks/getWorkflowTasks",
            getWorkflowPipelines: "tasks/getWorkflowPipelines",
            decWorkflowPipelineTaskCount: "tasks/decWorkflowPipelineTaskCount",
        }),
        createdTask(pipelineId, task) {
            this.$emit("updateTaskList", {
                ...this.localTasks,
                [pipelineId]: [task, ...(this.localTasks[pipelineId] || [])],
            });
            this.fetchPipelines(true);
        },
        closeCreateMode() {
            this.pipelines = this.pipelines.filter((col) => !col.isNewColumn);

            const workflow = this.workflows[this.workflowId];
            this.setWorkflows(
                Object.keys(this.workflows).map((workflowId) => {
                    if (workflowId === this.workflowId) {
                        return { ...workflow, pipeline: this.pipelines };
                    }
                    return this.workflows[workflowId];
                })
            );
        },
        updateScrollBar() {
            const board = document.querySelector(".kanban-board");
            if (!board || board.scrollWidth === 0) return;

            // Temel ölçümleri al
            this.boardWidth = board.scrollWidth;
            this.containerWidth = board.clientWidth;
            const scrollLeft = board.scrollLeft;

            // Minimap içeriğinin genişliği (toggle butonu hariç)
            const minimapContentWidth = 250 - 36 - 15;

            // Board henüz tam yüklenmemişse varsayılan değerler
            if (this.boardWidth <= this.containerWidth) {
                this.scrollBarWidth = 30;
                this.scrollBarPosition = 0;
                return;
            }

            // Görünür alan oranı (0-1 arası)
            const visibleRatio = Math.min(this.containerWidth / this.boardWidth, 1);

            // Slider genişliği, görünür alan oranına göre hesaplanır
            this.scrollBarWidth = Math.max(visibleRatio * minimapContentWidth, 30);

            // Eğer içerik tamamen görünür durumdaysa
            if (visibleRatio >= 1) {
                this.scrollBarPosition = 0;
                return;
            }

            // Scroll pozisyonu oranı (0-1 arası)
            const scrollRatio = scrollLeft / (this.boardWidth - this.containerWidth);

            // Slider için kullanılabilir hareket alanı
            const availableSpace = minimapContentWidth - this.scrollBarWidth;

            // Slider pozisyonu
            this.scrollBarPosition = Math.max(0, Math.min(scrollRatio * availableSpace, availableSpace));
        },
        startDragging(event) {
            this.isDragging = true;
            this.startX = event.clientX;
            window.addEventListener("mousemove", this.onDrag);
            window.addEventListener("mouseup", this.stopDragging);
        },
        onDrag(event) {
            if (!this.isDragging) return;

            const board = document.querySelector(".kanban-board");
            const dx = event.clientX - this.startX;
            this.startX = event.clientX;

            // Minimap içeriğinin genişliği (toggle butonu hariç)
            const minimapContentWidth = 250 - 36 - 15;

            // Görünür alan oranı (0-1 arası)
            const visibleRatio = Math.min(this.containerWidth / this.boardWidth, 1);

            // Eğer içerik tamamen görünür durumdaysa
            if (visibleRatio >= 1) return;

            // Slider için kullanılabilir hareket alanı
            const availableSpace = minimapContentWidth - this.scrollBarWidth;

            // Yeni pozisyonu hesapla ve sınırla
            this.scrollBarPosition = Math.max(0, Math.min(this.scrollBarPosition + dx, availableSpace));

            // Board scroll pozisyonunu güncelle
            const scrollRatio = this.scrollBarPosition / availableSpace;
            const newScrollLeft = scrollRatio * (this.boardWidth - this.containerWidth);

            // Scroll pozisyonunu güncelle
            board.scrollLeft = newScrollLeft;
        },
        stopDragging() {
            this.isDragging = false;
            window.removeEventListener("mousemove", this.onDrag);
            window.removeEventListener("mouseup", this.stopDragging);
        },
        startAutoScroll() {
            this.isDraggingTask = true;
            this.handleMouseMove = this.handleMouseMove.bind(this);
            window.addEventListener("mousemove", this.handleMouseMove);
        },
        handleMouseMove(event) {
            if (!this.isDraggingTask) return;

            const mouseX = event.clientX;
            const windowWidth = window.innerWidth;
            const board = document.querySelector(".kanban-board");

            if (!board) return;

            // Sağ kenarda mı?
            if (mouseX > windowWidth - this.SCROLL_ZONE_SIZE) {
                this.scrollBoard("right", board);
            }
            // Sol kenarda mı?
            else if (mouseX < this.SCROLL_ZONE_SIZE) {
                this.scrollBoard("left", board);
            }
        },
        scrollBoard(direction, board) {
            if (!board) return;

            if (this.autoScrollInterval) {
                cancelAnimationFrame(this.autoScrollInterval);
            }

            const scroll = () => {
                if (!this.isDraggingTask) return;

                if (direction === "right") {
                    board.scrollLeft += this.SCROLL_SPEED;
                } else if (direction === "left") {
                    board.scrollLeft -= this.SCROLL_SPEED;
                }

                // Minimap pozisyonunu güncelle
                this.updateScrollBar();

                this.autoScrollInterval = requestAnimationFrame(scroll);
            };

            this.autoScrollInterval = requestAnimationFrame(scroll);
        },
        stopAutoScroll() {
            this.isDraggingTask = false;
            if (this.autoScrollInterval) {
                cancelAnimationFrame(this.autoScrollInterval);
                this.autoScrollInterval = null;
            }
            window.removeEventListener("mousemove", this.handleMouseMove);
            // Minimap pozisyonunu son kez güncelle
            this.updateScrollBar();
        },
        openChangeWorkflowModal(task) {
            this.changeWorkflowModal = true;
            this.changeWorkflowData = task;
        },
        handleChangeWorkflow() {
            if (this.changeWorkflowData && this.changeWorkflowData._id && this.selectedWorkflow) {
                const oldWorkflowId = this.workflowId;
                const oldPipelineId = this.changeWorkflowData.pipelineStage;

                this.changeWorkflow({
                    taskId: this.changeWorkflowData._id,
                    workflowId: this.selectedWorkflow,
                    oldWorkflowId,
                    oldPipelineId,
                })
                    .then(() => {
                        this.publishTaskUpdate(this.selectedWorkflow);
                        this.publishTaskUpdate();
                        this.$emit("updateTaskList", {
                            ...this.localTasks,
                            [this.changeWorkflowData.pipelineStage]: this.localTasks[
                                this.changeWorkflowData.pipelineStage
                            ].filter((t) => t._id !== this.changeWorkflowData._id),
                        });
                        this.$notification.success({
                            message: "Görev Taşındı",
                            description: "Görev başka bir iş akışına taşındı.",
                        });
                        this.changeWorkflowModal = false;
                        this.selectedWorkflow = null;
                        this.fetchPipelines(true);
                    })
                    .catch((error) => {
                        console.error("Error changing workflow:", error);
                    });
            } else {
                console.error("Task ID or Workflow ID is missing", {
                    taskId: this.changeWorkflowData ? this.changeWorkflowData._id : null,
                    workflowId: this.selectedWorkflow,
                });
            }
        },
        handleCancel() {
            this.changeWorkflowModal = false;
            this.selectedWorkflow = null;
        },
        clearListener() {
            this.listeners.map((removeListener) => removeListener());
            this.listeners = [];
        },
        resetActions() {
            this.renderKey += 1;
        },
        handleDragStart() {
            this.moving = true;
            this.startAutoScroll();
        },
        moveActionEnd(event) {
            this.stopAutoScroll();
            console.log("moveActionEnd", event);
            const taskId = event.clone.id;
            const pipelineId = event.to.id;
            const fromPipelineId = event.from.id;
            const newIndex = event.newIndex;
            const oldIndex = event.oldIndex;
            const isSamePipeline = pipelineId === fromPipelineId;
            const tasks = this.localTasks[pipelineId] || [];

            if (oldIndex === newIndex && pipelineId === fromPipelineId) {
                console.log("Task not moved because it is in the same position.");
                return;
            }

            const newDraggableIndex = event.newDraggableIndex;
            const firstTask = tasks[newDraggableIndex - 1];
            const secondTask = tasks[newDraggableIndex];

            // Optimistic update - önce UI'ı güncelle
            let updatedList = {
                ...this.localTasks,
            };
            if (!isSamePipeline) {
                const tasks = this.localTasks[pipelineId] || [];
                updatedList = {
                    ...updatedList,
                    [pipelineId]: [
                        ...tasks.slice(0, newIndex),
                        {
                            ...this.localTasks[fromPipelineId][oldIndex],
                            pipelineStage: pipelineId,
                        },
                        ...tasks.slice(newIndex),
                    ],
                    [fromPipelineId]: this.localTasks[fromPipelineId].filter((task) => task._id !== taskId),
                };
            } else {
                const tasks = (this.localTasks[pipelineId] || []).filter((task) => task._id !== taskId);
                updatedList = {
                    ...updatedList,
                    [pipelineId]: [
                        ...tasks.slice(0, newIndex),
                        {
                            ...this.localTasks[pipelineId][oldIndex],
                            pipelineStage: pipelineId,
                        },
                        ...tasks.slice(newIndex),
                    ],
                };
            }

            // Hemen UI'ı güncelle
            this.$emit("updateTaskList", updatedList);

            // Sonra API çağrısını yap
            this.updateTaskPosition({
                taskId,
                payload: {
                    firstTaskId: firstTask?._id,
                    secondTaskId: secondTask?._id,
                    workflowId: this.workflowId,
                    pipelineStageId: pipelineId,
                    fromPipelineId,
                },
            }).then(() => {
                this.publishTaskUpdate();
                // Sadece pipeline count'ları güncelle
                this.fetchPipelines(true);
            }).catch((error) => {
                console.error("Task position update failed:", error);
                // Hata durumunda geri al
                this.$emit("updateTaskList", this.localTasks);
                this.$notification.error({
                    message: "Hata",
                    description: "Görev taşınırken bir hata oluştu."
                });
            });
        },
        movePipelineEnd(event) {
            this.stopAutoScroll();
            console.log("movePipelineEnd", event);
            this.moving = false;
            const workflow = this.workflows[this.workflowId];
            const pipeline = this.pipelines[event.newIndex];
            const oldIndex = event.oldIndex;
            const newIndex = event.newIndex;
            if (oldIndex === newIndex) {
                console.log("Pipeline not moved because it is in the same position.");
                return;
            }

            if (pipeline.isNewColumn) {
                this.pipelines = this.pipelines.map((col) => {
                    if (col._id === pipeline._id) {
                        return {
                            ...col,
                            order: newIndex + 1,
                        };
                    }
                    return col;
                });
                return;
            } else {
                this.updatePipelinePosition({
                    workflowId: this.workflowId,
                    pipelineId: pipeline._id,
                    position: event.newIndex + 1,
                });
                this.publishTaskUpdate();
            }

            this.setWorkflows(
                Object.keys(this.workflows).map((workflowId) => {
                    if (workflowId === this.workflowId) {
                        return { ...workflow, pipeline: this.pipelines };
                    }
                    return this.workflows[workflowId];
                })
            );
        },
        createPipelineForm(pipeline) {
            const newColumn = this.pipelines.findIndex((col) => col?.isNewColumn);
            if (newColumn >= 0) {
                return;
            }

            this.pipelines = this.pipelines.reduce((acc, col) => {
                const lastElement = [...acc].pop();
                acc.push({
                    ...col,
                    order: (lastElement?.order || 1) + 1,
                });
                if (col._id === pipeline._id) {
                    const newPipeline = {
                        _id: Date.now(),
                        name: null,
                        type: col.type,
                        color: col.color,
                        order: (lastElement?.order || 1) + 1,
                        taskCount: 0,
                        tasks: [],
                        isNewColumn: true,
                    };
                    acc.push(newPipeline);
                }
                return acc;
            }, []);

            const workflow = this.workflows[this.workflowId];
            this.setWorkflows(
                Object.keys(this.workflows).map((workflowId) => {
                    if (workflowId === this.workflowId) {
                        return { ...workflow, pipeline: this.pipelines };
                    }
                    return this.workflows[workflowId];
                })
            );

            const finalNewColumnIndex = this.pipelines.findIndex((col) => col?.isNewColumn);
            if (finalNewColumnIndex >= 0) {
                this.$nextTick(() => {
                    const element = document.getElementById(this.pipelines[finalNewColumnIndex]._id);
                    element?.scrollIntoView({ behavior: "smooth" });
                });
            }
        },
        createPipeline(pipeline) {
            this.createPipelineStage({
                workflowId: this.workflowId,
                color: pipeline.color,
                name: pipeline.name,
                type: pipeline.type,
                order: pipeline.order,
            }).then(() => {
                this.closeCreateMode();
                this.fetchPipelines(true);
                this.publishTaskUpdate();
                this.$notification.success({
                    message: this.$t("task.success"),
                    description: this.$t("task.successDesc"),
                });
            });
        },
        openDeleteTaskModal(task) {
            this.deleteTaskModal = true;
            this.deleteTaskData = task;
        },
        confirmModalOpen(task) {
            console.log("confirmModalOpen", task);

            const workflow = this.workflows[this.workflowId];

            if (workflow && workflow.showDoneTaskModal === true) {
                this.confirmTaskModal = true;
                this.confirmTaskData = task;
            } else {
                this.confirmTaskData = task;
                this.simpleConfirmModal = true;
            }
        },
        simpleDoneTask() {
            const task = this.confirmTaskData;
            this.doneTask({
                taskId: task._id,
            }).then((taskDone) => {
                publishMessage(SystemMessageTypes.TASK_DONE, {
                    taskDone,
                    workflowId: this.workflowId,
                });
                this.$emit("updateTaskList", {
                    ...this.localTasks,
                    [task.pipelineStage]: (this.localTasks[task.pipelineStage] || []).filter((t) => t._id !== task._id),
                });
                this.simpleConfirmModal = false;
                this.publishTaskUpdate();
                this.fetchPipelines(true);
            });
        },
        archivedModalOpen(task) {
            this.archivedTaskModal = true;
            this.archivedTaskData = task;
        },
        changeWorkflowTask(task) {
            this.openChangeWorkflowModal(task);
        },
        toCompletedTask(task) {
            this.confirmTaskData = task;
            this.markTaskAsCompleted();
        },
        async markTaskAsCompleted() {
            try {
                const taskId = this.confirmTaskData._id;
                const completedPipeline = this.pipelines.find((g) => g.type === "completed");
                if (!completedPipeline) {
                    throw new Error("No 'Completed' pipeline found.");
                }
                const pipelineId = completedPipeline._id;
                const fromPipelineId = this.confirmTaskData.pipelineStage;
                const firstTask = this.localTasks[pipelineId]?.[0];

                await this.updateTaskPosition({
                    taskId,
                    payload: {
                        firstTaskId: firstTask?._id,
                        workflowId: this.workflowId,
                        pipelineStageId: pipelineId,
                        fromPipelineId,
                    },
                });
                this.publishTaskUpdate();
                this.fetchPipelines(true);

                this.$emit("updateTaskList", {
                    ...this.localTasks,
                    [pipelineId]: [this.confirmTaskData, ...(this.localTasks[pipelineId] || [])],
                    [fromPipelineId]: (this.localTasks[fromPipelineId] || []).filter((task) => task._id !== taskId),
                });

                this.confirmTaskModal = false;
                this.$notification.success({
                    message: "Görev Tamamlandı",
                    description: "Görev tamamlananlar listesine taşındı.",
                });
                this.publishTaskUpdate();
            } catch (error) {
                console.error("Error updating task to completed:", error);
                this.$notification.error({
                    message: "Görev Taşınamadı",
                    description: "Görev tamamlananlar listesine taşınamadı.",
                });
            }
        },
        softDeleteTask() {
            this.deleteTask(this.deleteTaskData).then(() => {
                this.deleteTaskModal = false;
                this.publishTaskUpdate();
            });
            this.$emit("updateTaskList", {
                ...this.localTasks,
                [this.deleteTaskData.pipelineStage]: this.localTasks[this.deleteTaskData.pipelineStage].filter(
                    (t) => t._id !== this.deleteTaskData._id
                ),
            });
        },
        confirmTask(data) {
            const task = this.confirmTaskData;
            this.doneTask({
                taskId: task._id,
                status: data.status,
                description: data.description
            }).then((taskDone) => {
                publishMessage(SystemMessageTypes.TASK_DONE, {
                    taskDone,
                    workflowId: this.workflowId,
                });
                this.localTasks = {
                    ...this.localTasks,
                    [task.pipelineStage]: (this.localTasks[task.pipelineStage] || []).filter((t) => t._id !== task._id),
                };
                this.$emit("updateTaskList", this.localTasks);
                this.confirmTaskModal = false;
                this.publishTaskUpdate();
            });
        },
        archiveModalTask() {
            const task = this.archivedTaskData;
            this.toggleArchiveTask({
                workflowId: this.workflowId,
                taskId: task._id,
                isArchived: true,
            }).then(() => {
                this.localTasks = {
                    ...this.localTasks,
                    [task.pipelineStage]: (this.localTasks[task.pipelineStage] || []).filter((t) => t._id !== task._id),
                };
                this.$emit("updateTaskList", this.localTasks);
                this.$notification.success({
                    message: "Görev Arşivlendi",
                    description: "Görev arşive taşındı.",
                });
                this.archivedTaskModal = false;
                this.publishTaskUpdate();
            });
        },
        publishTaskUpdate(workflowId) {
            sendSocketMessage(COMMANDS.TASK_UPDATED, {
                siteId: this.$store.state.site.site._id,
                userId: this.$store.state.auth.user._id,
                workflowId: workflowId || this.workflowId,
            });
        },
        updatePipeline(pipeline) {
            this.publishTaskUpdate();
            this.updatePipelineStage({
                pipelineId: pipeline._id,
                params: {
                    color: pipeline.color,
                    name: pipeline.name,
                    type: pipeline.type,
                },
            }).then(() => {
                this.fetchPipelines(true);
            });
        },
        handlePipelineDeleted() {
            this.fetchPipelines(true);
            this.publishTaskUpdate();
        },
        scrollToPipeline(pipelineId) {
            const board = document.querySelector(".kanban-board");
            if (!board) return;

            const pipelineElement = document.getElementById(pipelineId);
            if (!pipelineElement) return;

            // Pipeline'ın sol kenarının pozisyonu
            const pipelineLeft = pipelineElement.offsetLeft;

            // Pipeline'ın genişliği
            const pipelineWidth = pipelineElement.offsetWidth;

            // Board'un görünür genişliği
            const boardVisibleWidth = board.clientWidth;

            // Pipeline'ı ortaya getirmek için gerekli scroll pozisyonu
            const scrollLeft = pipelineLeft - boardVisibleWidth / 2 + pipelineWidth / 2;

            // Smooth scroll ile pozisyona git
            board.scrollTo({
                left: Math.max(0, scrollLeft),
                behavior: "smooth",
            });

            // Scroll tamamlandıktan sonra minimap'i güncelle
            setTimeout(this.updateScrollBar, 500);
        },
        fetchTasksWithFilters() {
            this.$parent.isLoading = true;

            const params = {
                workflowId: this.workflowId,
                page: 1,
                perPage: 20,
                assignees: this.assignees,
                showOverdue: this.showOverdue,
                owner: this.owners,
                followers: this.followers,
                dateRange: this.dateRange,
                createdBy: this.createdBy,
                search: this.$parent?.searchQuery,
                priorities: this.$parent?.selectedPriorities,
                pipelines: this.selectedPipelines && this.selectedPipelines.length > 0
                    ? this.selectedPipelines
                    : null,
                tags: this.$parent?.selectedTags,
                startDateRange: this.startDateRange,
                endDateRange: this.endDateRange,
                updateAtRange: this.updateAtRange,
                sortBy: this.sortBy
            };

            return this.getWorkflowTasks(params)
                .then(tasksResult => {
                    console.log("Tasks loaded:", tasksResult);

                    this.$nextTick(() => {
                        const workflow = this.workflows[this.workflowId];
                        if (workflow && workflow.pipeline) {
                            this.pipelines = workflow.pipeline;
                            this.pipelineLoaded = true;
                            console.log("Pipelines set:", this.pipelines.length);
                        }

                        this.$emit("updateTaskList", tasksResult || {});
                        this.$parent.isLoading = false;

                        this.$nextTick(() => {
                            this.updateScrollBar();
                        });
                    });
                })
                .catch(error => {
                    console.error("Görevleri yüklerken hata:", error);
                    this.$notification.error({
                        message: "Hata",
                        description: "Görevler yüklenirken bir hata oluştu."
                    });
                    this.$parent.isLoading = false;
                });
        },
        loadMoreTask: _.debounce(function (pipelineId) {
            if (this.loading[pipelineId]) return;

            this.loading[pipelineId] = true;
            this.$refs[`pipeline_${pipelineId}`].isLoading = true;

            if (!this.paginations[pipelineId]) {
                this.paginations[pipelineId] = {
                    page: 1,
                    perPage: 20,
                };
            }

            this.paginations[pipelineId].page += 1;

            const params = {
                workflowId: this.workflowId,
                pipelineId,
                page: this.paginations[pipelineId].page,
                perPage: this.paginations[pipelineId].perPage,
                assignees: this.assignees,
                showOverdue: this.showOverdue,
                owner: this.owners,
                followers: this.followers,
                dateRange: this.dateRange,
                createdBy: this.createdBy,
                tags: this.$parent.selectedTags,
                startDateRange: this.startDateRange,
                endDateRange: this.endDateRange,
                updateAtRange: this.updateAtRange,
                priorities: this.$parent.selectedPriorities,
                pipelines: this.selectedPipelines,
                search: this.$parent.searchQuery,
                sortBy: this.sortBy
            };

            this.getPipelineTasks(params).then((tasks) => {
                if (!tasks || tasks.length === 0) {
                    delete this.paginations[pipelineId];
                } else {
                    this.$emit("updateTaskList", {
                        ...this.localTasks,
                        [pipelineId]: [...(this.localTasks[pipelineId] || []), ...tasks],
                    });
                }

                setTimeout(() => {
                    this.$refs[`pipeline_${pipelineId}`].isLoading = false;
                    this.loading[pipelineId] = false;
                }, 500);
            }).catch(error => {
                console.error(`Pipeline ${pipelineId} için görevler yüklenirken hata:`, error);
                this.$notification.error({
                    message: "Hata",
                    description: "Görevler yüklenirken bir hata oluştu."
                });
                this.$refs[`pipeline_${pipelineId}`].isLoading = false;
                this.loading[pipelineId] = false;
            });
        }, 300),
        refillPipelineTasks(pipelineIds) {
            var self = this;
            if (!self.workflowId || !pipelineIds || pipelineIds.length === 0) {
                return Promise.resolve();
            }
            var unique = [];
            for (var i = 0; i < pipelineIds.length; i++) {
                var id = pipelineIds[i];
                if (!id) {
                    continue;
                }
                if (unique.indexOf(id) === -1) {
                    unique.push(id);
                }
            }
            if (unique.length === 0) {
                return Promise.resolve();
            }
            var requestId = self.refillRequestId + 1;
            self.refillRequestId = requestId;
            for (var j = 0; j < unique.length; j++) {
                var pidClear = unique[j];
                delete self.paginations[pidClear];
                delete self.loading[pidClear];
                var refsClear = self.$refs["pipeline_" + pidClear];
                var colClear = Array.isArray(refsClear) ? refsClear[0] : refsClear;
                if (colClear) {
                    colClear.isLoading = false;
                }
            }
            return Promise.all(
                unique.map(function (pipelineId) {
                    var params = {
                        workflowId: self.workflowId,
                        pipelineId: pipelineId,
                        page: 1,
                        perPage: 20,
                        assignees: self.assignees,
                        showOverdue: self.showOverdue,
                        owner: self.owners,
                        followers: self.followers,
                        dateRange: self.dateRange,
                        createdBy: self.createdBy,
                        tags: self.$parent.selectedTags,
                        startDateRange: self.startDateRange,
                        endDateRange: self.endDateRange,
                        updateAtRange: self.updateAtRange,
                        priorities: self.$parent.selectedPriorities,
                        pipelines: self.selectedPipelines,
                        search: self.$parent.searchQuery,
                        sortBy: self.sortBy,
                    };
                    return self.getPipelineTasks(params).then(function (tasks) {
                        return { pipelineId: pipelineId, tasks: tasks || [] };
                    });
                })
            )
                .then(function (results) {
                    if (requestId !== self.refillRequestId) {
                        return;
                    }
                    var parentTasks = self.$parent && self.$parent.tasks;
                    var next = parentTasks ? Object.assign({}, parentTasks) : Object.assign({}, self.localTasks);
                    for (var k = 0; k < results.length; k++) {
                        var r = results[k];
                        next[r.pipelineId] = r.tasks;
                    }
                    self.$emit("updateTaskList", next);
                })
                .catch(function (error) {
                    if (requestId !== self.refillRequestId) {
                        return;
                    }
                    console.error("Pipeline görevleri yenilenirken hata:", error);
                    self.$notification.error({
                        message: "Hata",
                        description: "Görevler yüklenirken bir hata oluştu.",
                    });
                });
        },
        toggleMinimap() {
            this.minimapCollapsed = !this.minimapCollapsed;
            this.$nextTick(() => {
                if (!this.minimapCollapsed) {
                    setTimeout(this.updateScrollBar, 100);
                }
            });
        },
        init() {
            this.clearListener();
            this.$nextTick(() => {
                const board = document.querySelector(".kanban-board");
                if (board) {
                    // Board üzerinde eğer scroll eventi yaparsak scrollBar güncellenir
                    setTimeout(this.updateScrollBar, 1000);
                    board.addEventListener("scroll", this.updateScrollBar);
                    this.listeners.push(() => board.removeEventListener("scroll", this.updateScrollBar));
                }
            });
            // Pencere boyutu değiştiğinde scrollBar güncellenir
            window.addEventListener("resize", this.updateScrollBar);
            this.fetchPipelines();
            // İlk yüklemede biraz bekle, sonra güncelle
            setTimeout(this.updateScrollBar, 500);
            this.listeners.push(() => window.removeEventListener("resize", this.updateScrollBar));
        },
        resetComponent() {
            this.pipelines = [];
            this.isDragging = false;
            this.listeners = [];
            this.moving = false;
            this.deleteTaskModal = false;
            this.confirmTaskModal = false;
            this.simpleConfirmModal = false;
            this.archivedTaskModal = false;
            this.changeWorkflowModal = false;
            this.selectedWorkflow = null;
            this.confirmTaskData = {};
            this.archivedTaskData = {};
            this.deleteTaskData = {};
            this.changeWorkflowData = {};
            this.loadingText = "Yükleniyor...";
            this.minimapCollapsed = false;
            this.paginations = {};
            this.loading = {};
            this.refillRequestId = 0;
        },
        initPipelines() {
            console.log("initPipelines");
            const workflow = this.workflows[this.workflowId];
            if (workflow && workflow.pipeline && workflow.pipeline.length > 0) {
                this.pipelines = workflow.pipeline;
                this.pipelineLoaded = true;
                console.log("Pipelines initialized:", this.pipelines.length);
            } else if (!this.pipelines || this.pipelines.length === 0) {
                this.pipelineLoaded = false;
                console.log("No pipelines found in workflow, will wait for fetch");
            }
        },
        applyFilters(filters) {
            console.log('TaskDraggable - Filtreler uygulanıyor:', filters);

            this.isUpdatingFromParent = true;

            if (filters.assignees !== undefined) this.$emit('update:assignees', filters.assignees);
            if (filters.owners !== undefined) this.$emit('update:owners', filters.owners);
            if (filters.followers !== undefined) this.$emit('update:followers', filters.followers);
            if (filters.createdBy !== undefined) this.$emit('update:createdBy', filters.createdBy);
            if (filters.showOverdue !== undefined) this.$emit('update:showOverdue', filters.showOverdue);
            if (filters.selectedPriorities !== undefined) this.$emit('update:selectedPriorities', filters.selectedPriorities);
            if (filters.selectedTags !== undefined) this.$emit('update:selectedTags', filters.selectedTags);
            if (filters.selectedPipelines !== undefined) {
                this.$emit('update:selectedPipelines', filters.selectedPipelines);
            }
            if (filters.dateRange !== undefined) this.$emit('update:dateRange', filters.dateRange);
            if (filters.startDateRange !== undefined) this.$emit('update:startDateRange', filters.startDateRange);
            if (filters.endDateRange !== undefined) this.$emit('update:endDateRange', filters.endDateRange);
            if (filters.updateAtRange !== undefined) this.$emit('update:updateAtRange', filters.updateAtRange);
            if (filters.searchQuery !== undefined) this.$emit('update:search', filters.searchQuery);

            this.fetchPipelines();

            this.$nextTick(() => {
                this.isUpdatingFromParent = false;
            });
        },
        fetchPipelines(silent = false) {
            if (!silent) {
                this.fetchTasksWithFilters();
            }
            return Promise.resolve(this.pipelines);
        },
    },
    mounted() {
        this.init();
    },
    beforeDestroy() {
        this.clearListener();
        this.stopAutoScroll();
    },
    watch: {
        workflows: {
            deep: true,
            handler(newWorkflows, oldWorkflows) {
                const currentWorkflow = newWorkflows[this.workflowId];
                const oldWorkflow = oldWorkflows && oldWorkflows[this.workflowId];

                if (currentWorkflow && currentWorkflow.pipeline) {
                    const pipelineChanged = !oldWorkflow ||
                        JSON.stringify(currentWorkflow.pipeline.map(p => ({ _id: p._id, taskCount: p.taskCount }))) !==
                        JSON.stringify(oldWorkflow.pipeline.map(p => ({ _id: p._id, taskCount: p.taskCount })));

                    if (pipelineChanged) {
                        this.pipelines = currentWorkflow.pipeline;
                        this.pipelineLoaded = true;
                        this.$nextTick(() => {
                            this.updateScrollBar();
                        });
                    }
                }
            },
        },
        tasks: {
            handler(newTasks) {
                // Prop değişikliklerini local state'e yansıt
                this.localTasks = Object.assign({}, newTasks);
            },
            deep: true,
            immediate: true,
        },
        workflowId: {
            immediate: true,
            handler(newValue, oldValue) {
                if (newValue && newValue !== oldValue) {
                    this.refillRequestId = this.refillRequestId + 1;
                    this.$emit("updateTaskList", {});
                    this.localTasks = {};
                    this.paginations = {};
                    this.loading = {};
                    this.pipelineLoaded = false;

                    this.initPipelines();
                    this.$nextTick(() => {
                        this.fetchPipelines();
                    });
                } else if (newValue && !oldValue) {
                    this.initPipelines();
                    this.$nextTick(() => {
                        this.fetchPipelines();
                    });
                }
            },
        },
        dateRange: {
            handler(newVal) {
                if (newVal && newVal.length === 2 &&
                    typeof newVal[0] !== 'string' && newVal[0] && newVal[0].format) {
                    this.$emit("update:dateRange", [
                        newVal[0].format('YYYY-MM-DD'),
                        newVal[1].format('YYYY-MM-DD')
                    ]);
                }
            }
        },
        filterHash: _.debounce(function(newVal, oldVal) {
            if (newVal !== oldVal && !this.isUpdatingFromParent) {
                this.fetchPipelines();
                this.updateScrollBar();
            }
        }, 300),
        selectedPipelines: {
            handler(newVal, oldVal) {
                if (!_.isEqual(newVal, oldVal)) {
                    if (!this.isUpdatingFromParent) {
                        this.fetchPipelines();
                    } else {
                        this.fetchTasksWithFilters();
                    }
                }
            },
            deep: true
        },
        sortBy: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.fetchTasksWithFilters();
                }
            }
        },
        // Loading sırasında kanban-board scroll pozisyonunu kaybetmeden korur.
        // Pipeline'lar v-show ile gizlenince scrollWidth sıfırlandığı için scrollLeft de
        // sıfırlanır; loading bittiğinde geri yüklüyoruz.
        isLoading(newVal, oldVal) {
            const board = document.querySelector(".kanban-board");
            if (!board) return;

            if (newVal && !oldVal) {
                this.savedScrollLeft = board.scrollLeft;
            } else if (!newVal && oldVal && this.savedScrollLeft > 0) {
                const target = this.savedScrollLeft;
                this.$nextTick(() => {
                    this.$nextTick(() => {
                        const b = document.querySelector(".kanban-board");
                        if (!b) return;
                        const maxScroll = Math.max(0, b.scrollWidth - b.clientWidth);
                        b.scrollLeft = Math.min(target, maxScroll);
                        this.updateScrollBar();
                    });
                });
            }
        },
    },
};
</script>

<style lang="scss">
$color: #ec4b1d;
.scroll-controls {
    position: fixed;
    bottom: 20px;
    right: 20px;
    display: flex;
    gap: 10px;
    z-index: 999;
}

.scroll-button {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #1890ff;
    color: white;
    border: none;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    transition: background 0.2s;
}

.scroll-button:hover {
    background: #40a9ff;
}

.task-more-options {
    padding-top: 0 !important;

    .ant-popover-arrow {
        display: none !important;
    }

    .ant-popover-inner-content {
        box-shadow: none !important;
    }

    .ant-popover-inner-content {
        padding: 5px 0px;
    }

    .options-item {
        width: 200px;
        display: flex;
        align-items: center;
        padding: 5px 10px;
        gap: 10px;
        font-weight: 500;
        font-size: 15px;
        color: #55555a;
        cursor: pointer;

        &:hover {
            background-color: #f1f3f9;
        }

        .material-icons {
            color: #91a2b6;
        }
    }

    .bracket {
        width: 100%;
        height: 1px;
        margin: 3px 0px;
        background-color: #e6e9f2;
    }
}

.kanban-board {
    position: relative;
    padding-bottom: 40px; /* Scroll container için boşluk */
    padding: 10px;
    overflow: auto;
    width: 100%;
    transition: 1s all;
    height: 100%;
    isolation: isolate;

    &::-webkit-scrollbar {
        display: none;
        height: 0;
    }

    .batch-operation {
        margin-bottom: 20px;
    }

    .supsis-draggable {
        height: 100%;

        &-card {
            height: calc(100% - 50px);
        }

        .pipeline-details {
            .pipeline-detail {
                font-weight: bold;
                font-size: 14px;
                display: flex;
                align-items: center;
                gap: 10px;
            }
        }
    }

    .drag-container {
        display: flex;
        gap: 10px;
        height: 100%;
    }

    .drag-pipeline-columns {
        height: 100%;

        .drag-pipeline-column-head {
            display: flex;
            gap: 10px;
            min-width: 300px;
            min-height: 50px;
            background-color: white;
            padding: 10px;
            border-radius: 4px;
            position: relative;

            .pipeline-head-content {
                display: flex;
                justify-content: space-between;
                width: 100%;
                align-items: center;
            }

            &:hover {
                .basic-task-container {
                    display: flex !important;
                }

                .column-options {
                    display: flex;
                }
            }

            .column-options {
                display: none;
                align-items: center;
                justify-content: center;
                gap: 5px;

                .material-icons {
                    font-size: 18px;
                    cursor: pointer;
                }
            }

            .basic-task-container {
                position: absolute;
                align-items: center;
                justify-content: center;
                width: 100%;
                bottom: -10px;
                left: 0px;
                display: none;

                .basic-task-add {
                    background-color: white;
                    box-shadow: 0 4px 10px rgba(34, 34, 34, 0.15);
                    border-radius: 4px;
                    padding: 4px 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                }
            }

            .column-check {
                display: inline-flex;
                gap: 5px;
                justify-content: center;
                align-items: center;

                .kanban-checkbox {
                    display: flex;

                    .ant-checkbox.ant-checkbox-checked {
                        .ant-checkbox-inner {
                            border: 3px solid var(--pipeline-color) !important;
                            background-color: var(--pipeline-color) !important;
                        }
                    }

                    .ant-checkbox-checked::after {
                        border: 1px solid var(--pipeline-color) !important;
                    }

                    .ant-checkbox-wrapper:hover .ant-checkbox-inner,
                    .ant-checkbox:hover .ant-checkbox-inner,
                    .ant-checkbox-input:focus + .ant-checkbox-inner {
                        border: 1px solid var(--pipeline-color) !important;
                    }

                    .ant-checkbox-indeterminate .ant-checkbox-inner {
                        background-color: var(--pipeline-color);
                        border-color: var(--pipeline-color);

                        &::after {
                            height: 3px;
                            background-color: white;
                        }
                    }
                }
            }
        }

        .drag-pipeline-column-rows-length {
            font-size: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 15px;
            color: #5a5a5b;
        }
    }

    .drag-pipeline-rows {
        display: flex;
        align-items: center;
        flex-direction: column;
        gap: 4px;
        border-radius: 4px;
        overflow-y: auto;
        overflow-x: hidden;
        height: calc(100% - 50px); // 50px: Column head height
        padding: 4px;
        background: linear-gradient(to bottom, #d3d6dc, white);

        .drag-pipeline-row {
            position: relative;
            width: 300px;
            border: 2px solid var(--white);
            border-radius: 4px;
            background: var(--white);

            .row-check {
                display: none;
                position: absolute;
                top: 0;
                left: 0;
                border-top-left-radius: 2px;
                overflow: hidden;
            }

            &:hover,
            &.selected {
                .row-check {
                    display: block;

                    .kanban-checkbox {
                        display: flex;

                        .ant-checkbox-inner {
                            border: none !important;
                            background: var(--pipeline-bg-color);
                        }

                        .ant-checkbox-checked::after {
                            border: none !important;
                        }

                        .ant-checkbox.ant-checkbox-checked {
                            .ant-checkbox-inner {
                                border: none !important;
                                background-color: var(--pipeline-color) !important;
                            }
                        }
                    }
                }
            }

            &.select {
                border: 2px solid var(--pipeline-color);
            }

            .kanban-task-details {
                display: flex;
                align-items: center;
                justify-content: space-between;
                cursor: pointer;

                .kanban-task-alt-items {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    margin-top: 10px;

                    &-item {
                        display: flex;
                        align-items: center;
                        gap: 3px;
                        user-select: none;

                        .material-icons {
                            font-size: 14px;
                        }
                    }
                }
            }

            .drag-pipeline-row-card {
                border-radius: 5px;
                width: 100%;
                overflow: hidden;
                display: flex;
                min-height: 70px;

                .content {
                    cursor: pointer;
                    padding: 10px;
                    width: 100%;

                    .task-head {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        width: 100%;
                        cursor: pointer;

                        .task-title {
                            width: 90%;
                            font-weight: bold;
                            font-size: 14px;
                            color: #5a5a5b;
                            word-wrap: break-word;
                            max-width: 190px;
                        }
                    }

                    .task-more {
                        font-size: 17px;
                        font-weight: bold;
                    }

                    .task-users {
                        display: flex;
                        align-items: center;
                        margin-top: 5px;
                        gap: 2px;
                    }
                }
            }
        }
    }

    .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, rgba(245, 247, 250, 0.97) 0%, rgba(255, 255, 255, 0.99) 100%);
        backdrop-filter: blur(12px) saturate(1.2);
        -webkit-backdrop-filter: blur(12px) saturate(1.2);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 999;
        animation: fadeIn 0.3s ease-in-out;
        border-radius: 8px;
        overflow: hidden;

        &::before {
            content: '';
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(236, 75, 29, 0.06) 0%, transparent 40%);
            animation: floatBg 8s ease-in-out infinite;
            pointer-events: none;
        }

        .loading-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.9);
            padding: 36px 56px 32px;
            border-radius: 24px;
            box-shadow:
                0 20px 60px rgba(15, 23, 42, 0.08),
                0 4px 20px rgba(15, 23, 42, 0.04),
                inset 0 1px 0 rgba(255, 255, 255, 0.9);
            border: 1px solid rgba(255, 255, 255, 0.6);
            animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
            position: relative;
            z-index: 1;
        }

        .kanban-loader {
            width: 240px;
            height: 150px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .kanban-loader-svg {
            width: 100%;
            height: 100%;
            overflow: visible;

            .kl-col-bg {
                fill: #f8fafc;
                stroke: #e2e8f0;
                stroke-width: 1;
            }

            .kl-card {
                fill: #ffffff;
                stroke: #e5e7eb;
                stroke-width: 1;
                animation: klCardPulse 2.4s ease-in-out infinite;
            }

            .kl-col-1 .kl-card:nth-of-type(1) { animation-delay: 0s; }
            .kl-col-1 .kl-card:nth-of-type(2) { animation-delay: 0.15s; }
            .kl-col-2 .kl-card { animation-delay: 0.3s; }
            .kl-col-3 .kl-card { animation-delay: 0.45s; }

            .kl-flow-path {
                stroke-dashoffset: 0;
                animation: klDash 1.8s linear infinite;
            }

            .kl-moving-card {
                animation: klMoveCard 3.2s cubic-bezier(0.65, 0, 0.35, 1) infinite;
            }
        }

        .loading-text {
            margin-top: 20px;
            background: linear-gradient(90deg, #1f2937 0%, $color 50%, #1f2937 100%);
            background-size: 200% auto;
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
            color: transparent;
            font-size: 17px;
            font-weight: 600;
            letter-spacing: 0.3px;
            animation: shimmerText 2.5s linear infinite;
        }

        .loading-subtext {
            margin-top: 8px;
            color: #64748b;
            font-size: 13px;
            font-weight: 400;
            opacity: 0.9;
            text-align: center;
            max-width: 260px;
        }

        .loading-dots {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-top: 18px;
        }

        .loading-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: $color;
            opacity: 0.4;
            animation: klDotBounce 1.4s ease-in-out infinite;

            &:nth-child(2) {
                animation-delay: 0.2s;
            }

            &:nth-child(3) {
                animation-delay: 0.4s;
            }
        }
    }
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.6;
        transform: scale(1.05);
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes slideUp {
    from {
        transform: translateY(30px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

@keyframes shimmerText {
    0% {
        background-position: 200% center;
    }
    100% {
        background-position: -200% center;
    }
}

@keyframes floatBg {
    0%, 100% {
        transform: translate(0, 0) scale(1);
    }
    33% {
        transform: translate(10%, -10%) scale(1.1);
    }
    66% {
        transform: translate(-10%, 10%) scale(0.9);
    }
}

@keyframes klCardPulse {
    0%, 100% {
        opacity: 0.6;
        transform: translateY(0);
    }
    50% {
        opacity: 1;
        transform: translateY(-1px);
    }
}

@keyframes klDotBounce {
    0%, 80%, 100% {
        transform: translateY(0) scale(0.8);
        opacity: 0.4;
    }
    40% {
        transform: translateY(-6px) scale(1.1);
        opacity: 1;
    }
}

@keyframes klDash {
    0% {
        stroke-dashoffset: 0;
    }
    100% {
        stroke-dashoffset: -14;
    }
}

@keyframes klMoveCard {
    0% {
        transform: translate(16px, 90px);
        opacity: 0;
    }
    8% {
        opacity: 1;
    }
    25% {
        transform: translate(16px, 36px);
        opacity: 1;
    }
    32% {
        transform: translate(16px, 36px) scale(0.96);
        opacity: 0.85;
    }
    50% {
        transform: translate(94px, 66px);
        opacity: 1;
    }
    57% {
        transform: translate(94px, 66px) scale(0.96);
        opacity: 0.85;
    }
    78% {
        transform: translate(172px, 36px);
        opacity: 1;
    }
    92% {
        transform: translate(172px, 36px);
        opacity: 1;
    }
    100% {
        transform: translate(172px, 36px);
        opacity: 0;
    }
}

.minimap-container {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 999;
    width: 250px;
    height: 36px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    padding: 4px;
    display: flex;
    align-items: center;
    border: 1px solid #e8e8e8;
    transition: all 0.3s ease;
    opacity: 0.7;
    overflow: hidden;

    &.minimap-collapsed {
        width: 36px;
        height: 36px;
        padding: 0;
        border-radius: 50%;
        background: #1890ff;
        opacity: 0.6;
        transform: scale(0.9);

        &:hover {
            transform: scale(1);
            opacity: 0.9;
        }

        .minimap-toggle {
            color: white;
        }
    }

    &:hover {
        opacity: 1;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    .minimap-toggle {
        min-width: 36px;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: #1890ff;
        z-index: 10;
        position: relative;

        .material-icons {
            font-size: 18px;
        }
    }

    .minimap-content {
        display: flex;
        flex: 1;
        position: relative;
        height: 100%;
        width: calc(100% - 36px);
        overflow: hidden;
    }

    .minimap-scroll {
        display: flex;
        width: 100%;
        height: 100%;
        gap: 2px;
        align-items: center;
        position: relative;
        z-index: 1;
        padding: 0 5px 0 0px;

        .minimap-item {
            height: 75%;
            flex: 1;
            min-width: 12px;
            border-radius: 3px;
            cursor: pointer;
            transition: all 0.2s;
            position: relative;

            &:hover {
                transform: scaleY(1.1);
                box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
            }

            &::after {
                content: "";
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(255, 255, 255, 0.1);
                border-radius: 3px;
            }
        }
    }

    .minimap-slider {
        position: absolute;
        top: 4px;
        height: calc(100% - 8px);
        background: rgba(0, 0, 0, 0.3);
        border-radius: 4px;
        cursor: grab;
        z-index: 3;
        pointer-events: auto;
        border: 1px solid rgba(255, 255, 255, 0.3);
        min-width: 30px;
        transition: background 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;

        .material-icons {
            color: white;
            font-size: 16px;
        }

        &:hover {
            background: rgba(0, 0, 0, 0.4);
        }

        &:active {
            cursor: grabbing;
            background: rgba(0, 0, 0, 0.5);
        }
    }
}

.simple-confirm-modal-content {
    display: flex;
    padding: 16px 0;

    .modal-icon {
        margin-right: 16px;

        .material-icons {
            font-size: 40px;
            color: #52c41a;
        }
    }

    .modal-message {
        flex: 1;

        p {
            margin: 0 0 8px;
            font-size: 16px;
            font-weight: 500;
            color: #333;
        }

        .modal-description {
            font-size: 14px;
            font-weight: normal;
            color: #666;
        }
    }
}
</style>
